import React, { useCallback, useEffect, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { Pages } from '../Pages'
import { MenuItems } from '../enums/MenuItems'
import tenantApi from '../api/tenant/TenantApi'
import { AdminTenant } from '../api/tenant/models/adminGet/AdminGetRes'
import UserRolePill from '../components/UserRolePill'
import { UpdateUserMembershipReq } from '../api/tenant/models/updateUser/UpdateUserReq'
import {
    UserDto,
    UserRoles,
    UserTenantMembershipDto,
} from '../api/tenant/models/UserDto'
import { CreateUserMembershipReq } from '../api/tenant/models/createUser/CreateUserReq'
import { PersonDto } from '../api/person/models/PersonDto'
import PersonUi from '../components/PersonUi'
import LayoutSingleView from '../components/layout/LayoutSingleView'
import PersonSearchModal from '../uiV2/person/search/PersonSearchModal'

const ROLE_OPTIONS: UserRoles[] = [
    UserRoles.HM,
    UserRoles.TA,
    UserRoles.JOB_SEEKER,
]

function CreateUser(): React.JSX.Element {
    const [email, setEmail] = useState<string>('')
    const [isSearchPersonModalOpen, setIsSearchPersonModalOpen] =
        useState(false)
    const [linkedPerson, setLinkedPerson] = useState<PersonDto>()
    const navigate = useNavigate()
    const [searchParams] = useSearchParams()
    const tenantId = searchParams.get('tenantId')
    const [tenant, setTenant] = useState<AdminTenant>()
    const [selectedRoles, setSelectedRoles] = useState<UserRoles[]>([])
    const [isProductAdmin, setIsProductAdmin] = useState<boolean>(false)
    const [emailChecked, setEmailChecked] = useState(false)
    const [emailChecking, setEmailChecking] = useState(false)
    const [existingUser, setExistingUser] = useState<UserDto | undefined>()
    const [emailCheckError, setEmailCheckError] = useState<string>()

    const refreshTenant = useCallback(async () => {
        if (!tenantId) {
            return
        }

        const tenant = (await tenantApi.adminGet(tenantId)).tenant
        setTenant(tenant)
    }, [tenantId])

    useEffect(() => {
        refreshTenant()
    }, [tenantId, refreshTenant])

    useEffect(() => {
        setEmailChecked(false)
        setExistingUser(undefined)
        setEmailCheckError(undefined)
    }, [email])

    function handleRoleChange(
        event: React.ChangeEvent<HTMLInputElement>,
    ): void {
        const role = event.target.value as UserRoles
        if (event.target.checked) {
            setSelectedRoles((prev) => [...prev, role])
        } else {
            setSelectedRoles((prev) => prev.filter((r) => r !== role))
        }
    }

    function handleProductAdminChange(
        event: React.ChangeEvent<HTMLInputElement>,
    ): void {
        setIsProductAdmin(event.target.checked)
    }

    async function handleEmailCheck(): Promise<UserDto | null | undefined> {
        const trimmedEmail = email.trim()

        if (!trimmedEmail) {
            setEmailCheckError('Email is required')
            setEmailChecked(false)
            setExistingUser(undefined)
            return undefined
        }

        setEmailChecking(true)
        setEmailCheckError(undefined)

        try {
            const lookupResult = await tenantApi.lookupUserByEmail({
                email: trimmedEmail,
            })
            setExistingUser(lookupResult.user ?? undefined)
            setEmailChecked(true)
            return lookupResult.user ?? null
        } catch (error) {
            console.error('Email lookup failed', error)
            setEmailCheckError(
                'Failed to verify the email. Please try again or contact support.',
            )
            setEmailChecked(false)
            setExistingUser(undefined)
            return undefined
        } finally {
            setEmailChecking(false)
        }
    }

    async function saveUser() {
        if (!tenant) {
            throw new Error(
                `Unable to save user ${email} for tenant ${tenantId}. Tenant not found.`,
            )
        }

        const trimmedEmail = email.trim()

        if (!trimmedEmail) {
            alert('Email is required')
            return
        }

        let checkedUser = existingUser

        if (!emailChecked) {
            const emailCheckResult = await handleEmailCheck()

            if (emailCheckResult === undefined) {
                return
            }

            checkedUser = emailCheckResult ?? undefined
        }

        if (checkedUser) {
            await tenantApi.updateUser({
                userId: checkedUser.id,
                memberships: buildUpdatedMembershipsForExistingUser(
                    checkedUser.memberships,
                    tenant.id,
                    selectedRoles,
                ),
                isActive: checkedUser.isActive,
                isProductAdmin,
                personId: linkedPerson?.id ?? checkedUser.personId,
            })

            navigate(`${Pages.Tenant}?id=${tenant.id}`)
            return
        }

        const memberships: CreateUserMembershipReq[] = selectedRoles.length
            ? [
                  {
                      tenantId: tenant.id,
                      roles: selectedRoles,
                  },
              ]
            : []

        await tenantApi.createUser({
            email: trimmedEmail,
            memberships,
            isProductAdmin,
            personId: linkedPerson?.id,
        })
        navigate(`${Pages.Tenant}?id=${tenant?.id}`)
    }

    function emailChange(event: React.ChangeEvent<HTMLInputElement>): void {
        setEmail(event.target.value)
    }

    async function handleSelectPerson(person: PersonDto) {
        setLinkedPerson(person)
        setIsSearchPersonModalOpen(false)
    }

    async function handleRemovePerson() {
        setLinkedPerson(undefined)
        setIsSearchPersonModalOpen(false)
    }

    return (
        <LayoutSingleView activeMenuItem={MenuItems.Users}>
            <>
                {!tenant && <div>Loading...</div>}
                {tenant && (
                    <div>
                        <h1>Create New User for {tenant?.name}</h1>
                        <div className="space-y-6">
                            <div className="space-y-3">
                                <label htmlFor="email">Email*</label>
                                <div className="flex flex-col gap-2">
                                    <input
                                        id="email"
                                        type="text"
                                        placeholder="Email..."
                                        value={email}
                                        onChange={emailChange}
                                        className="form-input"
                                    ></input>
                                    <div className="flex flex-wrap items-center gap-2">
                                        <button
                                            className="secondary-button"
                                            onClick={handleEmailCheck}
                                            disabled={
                                                emailChecking || !email.trim()
                                            }
                                        >
                                            {emailChecking
                                                ? 'Checking...'
                                                : 'Check email'}
                                        </button>
                                        <span className="text-sm text-slate-500">
                                            {emailChecked &&
                                                (existingUser
                                                    ? 'Existing user found. We will attach them to this tenant.'
                                                    : 'No user found. We will create a new account.')}
                                        </span>
                                    </div>
                                </div>
                                {emailCheckError && (
                                    <div className="text-sm text-red-500">
                                        {emailCheckError}
                                    </div>
                                )}
                            </div>

                            {emailChecked && (
                                <div className="space-y-4">
                                    <div className="space-y-2">
                                        <div>
                                            <label>
                                                <input
                                                    type="checkbox"
                                                    id="admin"
                                                    name="isProductAdmin"
                                                    checked={isProductAdmin}
                                                    onChange={
                                                        handleProductAdminChange
                                                    }
                                                    className="mr-2"
                                                />
                                                <UserRolePill isProductAdmin />
                                            </label>
                                        </div>
                                        {ROLE_OPTIONS.map((role) => (
                                            <div key={role}>
                                                <label>
                                                    <input
                                                        type="checkbox"
                                                        id={`role-${role}`}
                                                        name="roles"
                                                        value={role}
                                                        onChange={
                                                            handleRoleChange
                                                        }
                                                        className="mr-2"
                                                    />
                                                    <UserRolePill
                                                        value={role}
                                                    />
                                                </label>
                                            </div>
                                        ))}
                                    </div>

                                    {!existingUser && (
                                        <div className="mt-4">
                                            <label htmlFor="personId">
                                                Link to Person (imported from
                                                Linked In)
                                            </label>
                                            {linkedPerson && (
                                                <div>
                                                    <div>
                                                        Selected person:{' '}
                                                        <PersonUi
                                                            person={
                                                                linkedPerson
                                                            }
                                                        />
                                                    </div>
                                                    <button
                                                        className="remove-button"
                                                        onClick={
                                                            handleRemovePerson
                                                        }
                                                    >
                                                        Remove
                                                    </button>
                                                </div>
                                            )}
                                            <div>
                                                <button
                                                    className="secondary-button"
                                                    onClick={() =>
                                                        setIsSearchPersonModalOpen(
                                                            true,
                                                        )
                                                    }
                                                >
                                                    Select Person
                                                </button>
                                            </div>
                                        </div>
                                    )}

                                    <div className="form-buttons">
                                        <button
                                            className="primary-button mt-4"
                                            onClick={saveUser}
                                            disabled={
                                                !emailChecked || emailChecking
                                            }
                                        >
                                            Save
                                        </button>
                                        <button
                                            className="danger-button"
                                            onClick={() =>
                                                navigate(
                                                    `${Pages.Tenant}?id=${tenant?.id}`,
                                                )
                                            }
                                        >
                                            Cancel
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>
                        {emailChecked &&
                            !existingUser &&
                            isSearchPersonModalOpen && (
                                <PersonSearchModal
                                    onClose={() =>
                                        setIsSearchPersonModalOpen(false)
                                    }
                                    onSelect={handleSelectPerson}
                                />
                            )}
                    </div>
                )}
            </>
        </LayoutSingleView>
    )
}

export default CreateUser

function buildUpdatedMembershipsForExistingUser(
    existingMemberships: UserTenantMembershipDto[] | undefined,
    tenantId: string,
    selectedRoles: UserRoles[],
): UpdateUserMembershipReq[] {
    const membershipMap = new Map<string, Set<UserRoles>>()

    for (const membership of existingMemberships ?? []) {
        membershipMap.set(membership.tenantId, new Set(membership.roles ?? []))
    }

    if (!membershipMap.has(tenantId)) {
        membershipMap.set(tenantId, new Set())
    }

    const targetRoles = membershipMap.get(tenantId)!
    for (const role of selectedRoles) {
        targetRoles.add(role)
    }

    membershipMap.set(tenantId, targetRoles)

    return Array.from(membershipMap.entries()).map(([id, roles]) => ({
        tenantId: id,
        roles: Array.from(roles),
    }))
}
